import React, { useState, useEffect } from 'react'
import Box from '@mui/material/Box'
import Tabs from '@mui/material/Tabs'
import Tab from '@mui/material/Tab'
import ToggleButton from '@mui/material/ToggleButton'
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup'
import { RoutePrefixContext, useTabRoute, childPrefix } from './route'

// `slug` is the stable, ASCII URL identity of a sub-tab (part of the hash path, e.g. the `forskott`
// in #/synk/forskott) — independent of the label wording and of which sub-tabs a role can see.
export type Panel = { slug: string, label: string, panel: (active: boolean) => React.ReactNode }

// A panel is mounted only once it first becomes the active sub-tab (of an active parent), then kept
// mounted and merely hidden — so its data isn't fetched until the user first opens it, and its state
// and fetched data survive later tab switches (e.g. Förskottstävlingar keeps its scraped payment
// events cached). This keeps the visible tab from waiting on hidden ones at load. Activation is
// tracked by slug so it survives capability-driven reordering.
//
// Each panel is rendered under its OWN prefix (ancestors + its slug), not the currently-active one —
// every panel is mounted at once, so a hidden nested sub-tab group must still know its true path to
// drive its own level of the hash. `active` (parent visible) AND (this is the active slug) decides
// which single panel is shown and which one activation latches onto.
function Panels({ panels, activeIndex, active, prefix }: {
    panels: Panel[], activeIndex: number, active: boolean, prefix: string,
}) {
    const activeSlug = active ? panels[activeIndex]?.slug : undefined
    const [activated, setActivated] = useState<Set<string>>(() => activeSlug ? new Set([activeSlug]) : new Set())
    useEffect(() => {
        if (activeSlug !== undefined) setActivated(s => s.has(activeSlug) ? s : new Set(s).add(activeSlug))
    }, [activeSlug])
    return <>{panels.map((p, i) =>
        <div key={p.slug} hidden={activeIndex !== i}>
            {activated.has(p.slug)
                ? <RoutePrefixContext.Provider value={childPrefix(prefix, p.slug)}>
                    {p.panel(active && activeIndex === i)}
                  </RoutePrefixContext.Provider>
                : null}
        </div>)}</>
}

// Underline tabs for switching between areas within a top-level tab (e.g. the three Eventor-sync
// areas, the two admin areas). Rendered a touch smaller than the primary app-nav tabs so a nested
// row reads as subordinate — differentiation by weight, not colour. The active area comes from (and
// a click pushes to) this level's segment of the URL hash.
export function SectionTabs({ panels, active }: { panels: Panel[], active?: boolean }) {
    const { prefix, activeIndex, select } = useTabRoute(panels.map(p => p.slug))
    return (
        <Box>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
                <Tabs value={activeIndex} onChange={(_event, v: number) => select(panels[v].slug)}
                      sx={{ minHeight: 40, '& .MuiTab-root': { minHeight: 40, py: 1, fontSize: '0.85rem' } }}>
                    {panels.map(p => <Tab key={p.slug} label={p.label}/>)}
                </Tabs>
            </Box>
            <Panels panels={panels} activeIndex={activeIndex} active={active ?? true} prefix={prefix}/>
        </Box>
    )
}

// A pill/segmented toggle for the finer "work vs settings" switch (Synk | Inställningar). Distinct
// in *form* from the underline tabs above it, so even when nested directly under area tabs it reads
// as a switch inside the view — and it looks identical wherever it appears (Swish and
// Förskottstävlingar) regardless of how deep that view sits. Route-driven like SectionTabs.
export function SegmentedTabs({ panels, active }: { panels: Panel[], active?: boolean }) {
    const { prefix, activeIndex, select } = useTabRoute(panels.map(p => p.slug))
    return (
        <Box>
            <ToggleButtonGroup exclusive size="small" value={activeIndex} sx={{ mb: 1.5 }}
                    onChange={(_event, v: number | null) => { if (v !== null) select(panels[v].slug) }}>
                {panels.map((p, i) =>
                    <ToggleButton key={p.slug} value={i} sx={{ textTransform: 'none', px: 2 }}>{p.label}</ToggleButton>)}
            </ToggleButtonGroup>
            <Panels panels={panels} activeIndex={activeIndex} active={active ?? true} prefix={prefix}/>
        </Box>
    )
}
